import { DownloadLink } from "./DownloadLink";
import { ArrowRight, CheckCircle2, Gift, MessageSquareText, PhoneCall, Sparkles } from "lucide-react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Lang, t } from "../i18n";
import { downloadPath } from "../routes";
import { reportLeadEntryConversion } from "../tracking";

type BillingCycle = "monthly" | "annual";
type PlanKey = "free" | "pro" | "premium";
type Feature = {
  title: string;
  detail?: string;
  note?: string;
  detailHighlight?: string;
};

const planKeys: PlanKey[] = ["free", "pro", "premium"];

const features: Record<PlanKey, Feature[]> = {
  free: [
    { title: "pricing.free.feature.1", detail: "pricing.free.feature.1.detail", note: "pricing.free.feature.1.note", detailHighlight: "pricing.free.feature.1.rate" },
    { title: "pricing.free.feature.2", detail: "pricing.free.feature.2.detail" },
    { title: "pricing.free.feature.3", detail: "pricing.free.feature.3.detail" },
    { title: "pricing.free.feature.5", detail: "pricing.free.feature.5.detail" },
    { title: "pricing.free.feature.7", detail: "pricing.free.feature.7.detail" },
  ],
  pro: [
    { title: "pricing.pro.feature.1", detail: "pricing.pro.feature.1.detail" },
    { title: "pricing.pro.feature.2", detail: "pricing.pro.feature.2.detail" },
    { title: "pricing.pro.feature.8", detail: "pricing.pro.feature.8.detail" },
    { title: "pricing.pro.feature.9", detail: "pricing.pro.feature.9.detail" },
    { title: "pricing.pro.feature.10", detail: "pricing.pro.feature.10.detail" },
  ],
  premium: [
    { title: "pricing.premium.feature.9", detail: "pricing.premium.feature.9.detail" },
    { title: "pricing.premium.feature.1", detail: "pricing.premium.feature.1.detail" },
    { title: "pricing.premium.feature.10", detail: "pricing.premium.feature.10.detail" },
    { title: "pricing.premium.feature.2", detail: "pricing.premium.feature.2.detail" },
    { title: "pricing.premium.feature.7", detail: "pricing.premium.feature.7.detail" },
  ],
};

const prices: Record<BillingCycle, Record<PlanKey, number>> = {
  monthly: {
    free: 0,
    pro: 49,
    premium: 99,
  },
  annual: {
    free: 0,
    pro: 39,
    premium: 79,
  },
};

function BillingToggle({
  billing,
  onChange,
  lang,
}: {
  billing: BillingCycle;
  onChange: (billing: BillingCycle) => void;
  lang: Lang;
}) {
  return (
    <div className="inline-flex rounded-full border border-[#e7dce7] bg-white p-1 shadow-[0_12px_28px_rgba(42,19,41,.07)]">
      {(["monthly", "annual"] as BillingCycle[]).map((item) => {
        const active = billing === item;
        return (
          <button
            key={item}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(item)}
            className={`inline-flex h-9 items-center justify-center gap-2 rounded-full px-5 text-[13px] font-extrabold transition-colors ${
              active ? "bg-[#17141c] text-white" : "text-[#5f5565] hover:text-[#100f12]"
            }`}
          >
            {t(lang, `pricing.billing.${item}`)}
            {item === "annual" ? (
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] ${active ? "bg-white text-[#5223b1]" : "bg-[#f3eefa] text-[#5223b1]"}`}>
                {t(lang, "pricing.billing.save")}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function PlanIcon({ plan }: { plan: PlanKey }) {
  const Icon = plan === "free" ? Gift : plan === "pro" ? MessageSquareText : PhoneCall;

  return (
    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#f3eefa] text-[#5b24c9]">
      <Icon size={22} strokeWidth={2.5} />
    </span>
  );
}

function FeatureItem({ feature, lang }: { feature: Feature; lang: Lang }) {
  return (
    <li className="flex gap-2.5 text-[#5f5565] lg:min-h-[52px]">
      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#5b24c9]" strokeWidth={2.6} />
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] font-extrabold leading-[1.25] text-[#4d4652]">
          <span>{t(lang, feature.title)}</span>
          {feature.note && (
            <span className="max-w-full rounded-md bg-[#f3eefa] px-1.5 py-0.5 text-[11px] font-bold leading-[1.35] text-[#5223b1]">
              {t(lang, feature.note)}
            </span>
          )}
        </span>
        {feature.detail ? (
          <span className="mt-0.5 block text-[11px] font-semibold leading-[1.45] text-[#8a7f8f]">
            {t(lang, feature.detail)}
            {feature.detailHighlight && <>{" "}<strong className="font-extrabold text-[#5223b1]">{t(lang, feature.detailHighlight)}</strong></>}
          </span>
        ) : null}
      </span>
    </li>
  );
}

function PlanCard({ plan, billing, lang }: { plan: PlanKey; billing: BillingCycle; lang: Lang }) {
  const featured = plan === "pro";
  const free = plan === "free";
  const { pathname, search } = useLocation();

  return (
    <article
      className={`relative flex h-full flex-col rounded-[20px] border bg-white p-5 shadow-[0_22px_58px_rgba(34,19,48,.07)] sm:p-6 ${
        featured ? "border-[#5b24c9] ring-1 ring-[#5b24c9]" : "border-[#eaddec]"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <PlanIcon plan={plan} />
            <div>
              <h3 className="text-[27px] font-extrabold leading-none tracking-[-0.03em] text-[#17141c] sm:text-[30px]">
                {t(lang, `pricing.${plan}.name`)}
              </h3>
            </div>
          </div>
        </div>
        {featured ? (
          <div className="inline-flex h-8 flex-shrink-0 items-center justify-center rounded-full bg-[#5b24c9] px-4 text-[12px] font-black text-white shadow-[0_12px_30px_rgba(91,36,201,.18)]">
            {t(lang, "pricing.popular")}
          </div>
        ) : null}
      </div>

      <p className="mt-4 max-w-[500px] text-[14px] font-semibold leading-6 tracking-[-0.01em] text-[#4d4652] lg:min-h-[48px]">
        {t(lang, `pricing.${plan}.summary`)}
      </p>

      <div className="mt-3 flex items-end gap-2">
        <span className="text-[46px] font-black leading-none tracking-[-0.06em] text-[#17141c] sm:text-[50px]">
          ${prices[billing][plan]}
        </span>
        <span className="pb-1.5 text-[14px] font-extrabold text-[#7b7380]">{t(lang, "pricing.perMonth")}</span>
      </div>
      <p className="mt-1.5 text-[12px] font-bold leading-5 text-[#8a7f8f]">
        {t(lang, free ? "pricing.free.note" : billing === "annual" ? "pricing.annual.note" : "pricing.monthly.note")}
      </p>

      <div className="mt-4 h-px w-full bg-[#eee4ef]" />

      <ul className="mt-4 grid gap-x-4 gap-y-3 lg:gap-y-0">
        {features[plan].map((feature) => (
          <FeatureItem key={feature.title} feature={feature} lang={lang} />
        ))}
      </ul>

      <div className="mt-auto pt-5">
        <DownloadLink
          to={downloadPath(lang, `pricing_${plan}_${billing}`, plan, search, pathname)}
          onClick={() => reportLeadEntryConversion(search)}
          className={`inline-flex h-12 w-full items-center justify-center gap-2 rounded-full px-6 text-[14px] font-extrabold leading-none no-underline transition-transform hover:-translate-y-0.5 ${
              featured
              ? "bg-[#5b24c9] text-white shadow-[0_18px_46px_rgba(91,36,201,.24)]"
              : "border border-[#e3d6e6] bg-white text-[#17141c] hover:bg-[#fbf8fc]"
          }`}
        >
          <span className="leading-none">{t(lang, `pricing.${plan}.cta`)}</span>
          <ArrowRight className="h-4 w-4 flex-shrink-0 translate-y-px" strokeWidth={2.7} />
        </DownloadLink>
      </div>
    </article>
  );
}

export function Pricing({ lang, showIntro = true }: { lang: Lang; showIntro?: boolean }) {
  const [billing, setBilling] = useState<BillingCycle>("annual");

  return (
    <section id="pricing" data-dental-centered-anchor={!showIntro || undefined} className="relative overflow-hidden bg-[#f8f9fa] px-5 py-12 sm:px-10 lg:py-14">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(248,249,250,0)_0%,rgba(246,240,252,.72)_48%,rgba(248,249,250,0)_100%)]" />
      <div className="relative mx-auto max-w-[var(--dental-content-width,1240px)]">
        <div className={`mx-auto text-center ${showIntro ? "max-w-[760px]" : "max-w-none"}`}>
          {showIntro && (
            <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-[#f3eefa] px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#5223b1]">
              <Sparkles size={14} strokeWidth={2.6} />
              {t(lang, "pricing.label")}
            </div>
          )}
          <h2 className={`${showIntro ? "mt-4" : "lg:whitespace-nowrap"} text-[34px] font-extrabold leading-[1.06] tracking-[-0.045em] text-[#100f12] sm:text-[46px] lg:text-[52px]`}>
            {t(lang, "pricing.title")}
          </h2>
          {showIntro && (
            <p className="mx-auto mt-4 max-w-[640px] text-[15px] font-semibold leading-7 tracking-[-0.01em] text-[#5f5565]">
              {t(lang, "pricing.sub")}
            </p>
          )}
          <div className="mt-5">
            <BillingToggle billing={billing} onChange={setBilling} lang={lang} />
          </div>
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3 lg:items-stretch">
          {planKeys.map((plan) => (
            <PlanCard key={plan} plan={plan} billing={billing} lang={lang} />
          ))}
        </div>
      </div>
    </section>
  );
}
